import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

import * as profileSelectors from '@src/containers/ProfileVenue/selectors';
import { hooks as infoModalsHooks } from '@src/containers/InfoModals';

import HeaderGallery from '@src/components/HeaderGallery/connectors/venueProfile';
import ProfileSubmenuComp from '@src/components/ProfileSubmenu/component';
import FitEvents from '@src/components/FitEvents/connectors/default';
import VenueFeatures from '@src/components/VenueFeatures/connectors/default';
import FilesList from '@src/components/FilesList/connectors/venueProfile';
import Sidebar from '@src/components/ProfileAside/connectors/default';
import ProfileAlbums from '@src/components/ProfileAlbums/connectors/default';
import Title from '@src/components/ProfileTitle/connectors/venue';
import ProfileFeedSummary from '@src/components/ProfileFeedSummary/connectors/default';
import FeedbacksList from '@src/components/FeedbacksList/connectors/profileVenue';
import ProfilePack from '@src/components/ProfilePack/connectors/venueProfile';
import RoomsList from '@src/components/RoomsList/connectors/default';
import RoomsListCS from '@src/components/RoomsList/cleanState';
import VenueFeaturesCS from '@src/components/VenueFeatures/noDataState';
import GiftLineComponent from '@src/components/GiftLine';
import { generateLink, LINK_TYPES } from '@src/libs/utils/links';
import { scrollToElement } from '@src/libs/utils/scrollTo';
import AdminProfileCDefault from '@src/components/AdminProfile/connectors/default';
import { isAdmin as getIsAdmin } from '@src/containers/Session/selectors';
import ViewInfo, { VIEWINFO_PARAMS } from '@old/components/blocks/ViewInfo';

import PageProfileClean from '@src/components/PageProfile/cleanState';
import { getProfileDetails } from '@src/containers/ProfileVenue/selectors/getProfileDetails';
import BreadcrumbsCSchema from '@src/components/Breadcrumbs/connectors/schema';
import ProfileAlbumsFuncCVenue from '@src/components/ProfileAlbumsFunc/connectors/ProfileAlbumsFuncCVenue';
import { getFuncAlbums } from '@src/containers/ProfileVenue/selectors/getFuncAlbums';

import VenueProfileComponent from '..';

const VenueProfileConnector = () => {
	const profileId = useSelector(profileSelectors.profileIdSelector);
	const profileAlbums = useSelector(profileSelectors.profileAlbums);
	const rateQtty = useSelector(profileSelectors.rateQtty);
	const isHasGift = useSelector(profileSelectors.isHasGift);
	const giftText = useSelector(profileSelectors.giftText);
	const files = useSelector(profileSelectors.mappedFiles);
	const userLink = useSelector(profileSelectors.profileUrl);
	const loaders = useSelector(profileSelectors.loadersSelector);
	const packs = useSelector(profileSelectors.tablePacks);
	const isNoFeaturesData = useSelector(profileSelectors.isNoFeaturesData);
	const rooms = useSelector(profileSelectors.profileRooms);
	const isOwner = useSelector(profileSelectors.isProfileOwner);
	const isNoDataProvided = useSelector(profileSelectors.isNoDataProvided);
	const isAdmin = useSelector(getIsAdmin);
	const isNoDataState = isOwner && isNoDataProvided;
	const userId = useSelector(profileSelectors.profileUserId);
	const details = useSelector(getProfileDetails);
	const { albums: funcAlbums } = useSelector(getFuncAlbums);

	const AdminPanel = React.useCallback(() => {
		return (
			<AdminProfileCDefault
				targetId={profileId}
				userId={userId}
				userType="Restaurant"
			/>
		);
	}, [profileId, userId]);

	const allFeedBacksLink = userLink
		? generateLink(LINK_TYPES.USER_RATINGS, {
				userLink,
		  })
		: null;

	const { openGiftModal } = infoModalsHooks.useActions();

	const menuItems = useMemo(() => {
		const items = [];
		items.push({ label: t('Обзор'), href: '#overview' });

		if (isNoDataState || rooms.length > 0) {
			items.push({ label: t('Пространства'), href: '#rooms' });
		}
		if (isNoDataState || !isNoFeaturesData) {
			items.push({ label: t('Описание'), href: '#features' });
		}
		if (packs.length > 0) {
			items.push({ label: t('Пакетные предложения'), href: '#packs' });
		}
		if (funcAlbums.length) {
			items.push({ label: t('Альбомы'), href: '#albums' });
		}
		if (rateQtty > 0) {
			items.push({ label: t('Отзывы'), href: '#rates' });
		}
		return items;
	}, [
		rateQtty,
		isNoFeaturesData,
		packs.length,
		rooms.length,
		funcAlbums.length,
		isNoDataState,
	]);

	const handleClickSubmenu = React.useCallback((e, item) => {
		const targetId = item.href;
		const targetEl = document.getElementById(targetId.replace('#', ''));
		if (targetEl) {
			scrollToElement(targetEl, { offset: -50 });
		}
		e.preventDefault();
	}, []);

	const GiftLine = useMemo(() => {
		return isHasGift
			? () => (
					<GiftLineComponent
						description={giftText}
						onClickMore={openGiftModal}
					/>
			  )
			: null;
	}, [isHasGift, giftText, openGiftModal]);

	const ProfileSubmenu = useMemo(() => {
		return () => (
			<ProfileSubmenuComp
				items={menuItems}
				onClickItem={handleClickSubmenu}
			/>
		);
	}, [menuItems, handleClickSubmenu]);

	const NoDataState = React.useCallback(() => {
		return (
			<>
				<RoomsListCS id="rooms" />
				<VenueFeaturesCS id="features" />
			</>
		);
	}, []);

	const user = useSelector(profileSelectors.profileDetails);

	if (!loaders.entity.isDataAvail) {
		return <PageProfileClean />;
	}

	return (
		<>
			<BreadcrumbsCSchema />
			<ViewInfo
				contentId={user.id}
				defaultRole={user.viewInfo?.contentType}
				param={VIEWINFO_PARAMS.PROFILE}
			/>
			<VenueProfileComponent
				TitleRP={Title}
				AlbumFunc={
					funcAlbums.length > 0 ? ProfileAlbumsFuncCVenue : undefined
				}
				schema={
					details?.schema
						? JSON.stringify(details?.schema)
						: undefined
				}
				allFeedBacksLink={allFeedBacksLink}
				rateQtty={rateQtty}
				albumsQtty={profileAlbums.length}
				albumFuncQtty={funcAlbums.length}
				HeaderGallery={HeaderGallery}
				ProfileSubmenu={ProfileSubmenu}
				FitEvents={FitEvents}
				VenueFeatures={isNoFeaturesData ? null : VenueFeatures}
				FilesList={files.length > 0 ? FilesList : null}
				SidebarRP={Sidebar}
				ProfileAlbums={profileAlbums.length > 0 ? ProfileAlbums : null}
				ProfileFeedSummary={ProfileFeedSummary}
				FeedbacksList={rateQtty > 0 ? FeedbacksList : null}
				GiftLine={GiftLine}
				RoomsList={rooms?.length > 0 ? RoomsList : null}
				PackRP={packs.length > 0 ? ProfilePack : null}
				counterPack={packs.length}
				NoDataState={isNoDataState ? NoDataState : null}
				AdminPanel={isAdmin ? AdminPanel : null}
			/>
		</>
	);
};

export default VenueProfileConnector;
